import React, { ForwardedRef, ReactNode, forwardRef } from 'react'
import { CSSTransition } from 'react-transition-group'

interface FadeTransitionProps {
	in?: boolean
	timeout: number
	children: ReactNode
}

/**
 * FadeTransition Component
 * 
 * This component is a functional React component that provides a fade transition effect
 * for its children using the CSSTransition component from the react-transition-group library.
 * It supports forwarding refs to the underlying DOM element.
 * 
 * @component
 * @param {Object} props - The properties object.
 * @param {boolean} [props.in] - Whether the transition is in progress.
 * @param {number} props.timeout - The duration of the transition in milliseconds.
 * @param {ReactNode} props.children - The content to be wrapped by the transition.
 * @param {ForwardedRef<HTMLDivElement>} ref - The forwarded ref to the underlying DOM element.
 * 
 * @example
 * // Usage example:
 * <FadeTransition in={true} timeout={300}>
 *   <div>Content to be faded in and out</div>
 * </FadeTransition>
 */
const FadeTransition = forwardRef(
	(props: FadeTransitionProps, ref: ForwardedRef<HTMLDivElement>) => {
		FadeTransition.displayName = 'FadeTransition'

		return (
			<CSSTransition
				in={props.in}
				timeout={props.timeout}
				unmountOnExit={true}
				classNames="fade"
				nodeRef={ref}
			>
				{props.children}
			</CSSTransition>
		)
	}
)
export default React.memo(FadeTransition)
